import React, { useEffect, useRef, useState } from 'react';
import MUIDataTable from "mui-datatables";
import SellerContainer from 'components/sellerContainer';
import ContainerHeader from 'components/ContainerHeader';
import AxiosRequest from 'sagas/axiosRequest';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { styles } from "constants/muiTableStyles";
import PaginationCount from "components/PaginationCount";
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Row, Col, Label, FormGroup, Input, FormFeedback, FormText } from 'reactstrap';
import { Form, Field, useForm } from 'react-final-form';
import AutoCompleteSearch from 'components/asyncAutocmplete';
import FieldFileInput from 'components/FieldFileInput';
import { required, number, requiredImage } from 'constants/validations';

const PER_PAGE = 50;
const STATUS_OPTIONS = ['all', 'draft', 'pending', 'active', 'paused', 'closed', 'expired', 'rejected'];
const TYPE_OPTIONS = ['all', 'coupon', 'cashback', 'gift'];
const DEFAULT_FILTER = { search: '', status: 'all', type: 'all', from: '', to: '' };
const DEFAULT_DRAFT = { status: 'all', type: 'all', from: '', to: '' };

const statusStyle = (s) => ({
  active: 'text-white bg-success',
  pending: 'text-white bg-warning',
  paused: 'text-white bg-info',
  rejected: 'text-white bg-danger',
  closed: 'text-white bg-grey',
  expired: 'text-white bg-grey',
  draft: 'text-white bg-grey'
}[s] || 'text-white bg-grey');

const cap = (s) => (s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1));
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
// Always two decimals. A rounding note is unreadable if one side prints as "100.5" and the other "101".
const money2 = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Who ended a campaign, worded for the seller reading it: an admin is 'Medimny' from this side of the
// app, and a threshold close was nobody's decision at all. A missing `closed_by` falls through to the
// system wording (rows closed before the field existed), exactly as the admin screen reads it.
const closedByLabel = (row) => ({
  seller: 'Closed by you',
  admin: 'Closed by Medimny',
  system: 'Closed automatically'
}[row.closed_by] || 'Closed automatically');

/*
The band a percentage cashback pays in, in words: "at least X" for the guaranteed floor and "up to Y"
for the ceiling above which nothing more is earned. Returns the inside of the bracket, or '' when the
campaign carries neither end.

Both halves are conditional. Only the 'minimum' type guarantees a floor, and a campaign approved
before `cashback_max` existed carries no ceiling - so a floored campaign reads "at least ₹150, up to
₹500", a plain percentage reads "up to ₹500", and a legacy one falls back to no bracket at all rather
than printing "up to ₹0", which would read as a cashback of nothing.

Same wording as the buyer side (the cart offer banners and the view-seller offer panel), so the
Reward column here describes the campaign exactly as the seller's buyers will see it.
*/
const cashbackBand = (type, floor, max) => [
  type === 'minimum' && Number(floor) > 0 ? `at least ${money(floor)}` : '',
  Number(max) > 0 ? `up to ${money(max)}` : ''
].filter(Boolean).join(', ');

/*
The total budget is stored as whole rupees - the API rounds it in createCampaign/editCampaign.

Budget is only ever drawn down by amounts that are themselves whole rupees (a cashback is rounded by
computeCashback, a coupon value is a multiple of 5), so paise on the cap could only ever sit there as
a remainder nothing can spend. Rounding it makes the cap mean what it says.

Returns null unless the figure actually has decimals - an integer budget is stored exactly as typed.
*/
const budgetRoundingNote = (values) => {
  const b = Number(values.budget_total);
  if (!isFinite(b) || b <= 0 || Number.isInteger(b)) return null;
  return `Budget will be saved as ${money2(Math.round(b))} - it is rounded to the nearest rupee.`;
};

/*
What this campaign will ACTUALLY pay out, when that differs from what the seller has typed.

A cashback reaches the buyer's wallet as a whole number of rupees - the API rounds it in
models/productCoupon.js `computeCashback`, and that same rounded figure is what is recovered from the
seller at settlement. So a decimal entered here is not the amount anyone will be charged or credited,
and the seller should learn that while they can still change it, not from a settlement line later.

Returns null when there is nothing to warn about, so the note simply does not render.

- flat: only when the amount actually has decimals - an integer flat cashback pays exactly as typed.
- percentage (with or without a floor): shown as soon as a percent is entered, because a percentage of
  an arbitrary order value lands on a whole rupee only by luck. Worked at the minimum order value, the
  one order size known in advance, so the note names a real figure rather than stating a rule.

Kept deliberately identical to the admin's copy in medimny-admin ProductCouponsList.js: the two forms
author the same kind of campaign against the same API, and a seller and an admin must not be told
different things about the same arithmetic.
*/
const cashbackRoundingNote = (values) => {
  const type = values.cashback_type;
  if (type === 'flat') {
    const v = Number(values.cashback_value);
    if (!isFinite(v) || v <= 0 || Number.isInteger(v)) return null;
    return `${money2(v)} will be paid out as ${money2(Math.round(v))} - cashback is rounded to the nearest rupee.`;
  }
  const pct = Number(values.cashback_percent);
  if (!isFinite(pct) || pct <= 0) return null;
  const parts = ['Cashback is rounded to the nearest rupee when it is paid out.'];
  const mov = Number(values.min_order_value);
  if (isFinite(mov) && mov > 0) {
    const atMov = (mov * pct) / 100;
    // Only worth working through when the example actually rounds. "₹150.00 would be paid as
    // ₹150.00" teaches nothing and makes the sentences that DO matter harder to notice.
    if (!Number.isInteger(atMov)) {
      parts.push(`At the ${money(mov)} minimum, ${pct}% = ${money2(atMov)} would be paid as ${money2(Math.round(atMov))}.`);
    }
  }
  if (type === 'minimum') {
    const floor = Number(values.cashback_floor);
    if (isFinite(floor) && floor > 0 && !Number.isInteger(floor)) {
      parts.push(`The ${money2(floor)} guaranteed minimum would be paid as ${money2(Math.round(floor))}.`);
    }
  }
  return parts.join(' ');
};

const composeValidators = (...validators) => (value, allValues) =>
  validators.reduce((error, validator) => error || validator(value, allValues), undefined);

// coupon value must be >= 20 and a multiple of 5 (flat INR)
const couponValueRule = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const v = Number(value);
  if (v < 20) return 'Minimum value is 20';
  if (v % 5 !== 0) return 'Must be a multiple of 5';
  return undefined;
};

// coupon value cannot exceed the total budget (only when a budget cap is set)
const withinBudgetRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  // Compared against the ROUNDED budget - that is the figure the API stores and re-checks this
  // against, so comparing against the raw entry could block a value the API would accept.
  const budget = Math.round(Number(allValues && allValues.budget_total));
  if (Number.isFinite(budget) && budget > 0 && Number(value) > budget) {
    return 'Coupon value cannot be greater than the total budget';
  }
  return undefined;
};

// How many orders a product coupon's budget can fund - the CEILING on the order count limit, and
// what that field is filled in with when the seller leaves it blank. One order draws exactly the
// coupon value, so the count is a whole division: a 650 budget on a 100 coupon funds 6 orders, not
// 7, because the 7th could not be paid in full (550 funds 5, not 6). Returns '' while either figure
// is missing, so nothing is guessed from a half-filled form.
const derivedCouponOrderLimit = (values) => {
  // Rounded first, because the API stores the budget rounded and derives this limit from the STORED
  // figure. Previewing it off the raw entry would show a limit the campaign is never created with:
  // a ₹999.60 budget on a ₹100 coupon previews 9 unrounded but is created as ₹1,000, funding 10.
  const budget = Math.round(Number(values.budget_total));
  const value = Number(values.coupon_value);
  if (!Number.isFinite(budget) || budget <= 0) return '';
  if (!Number.isFinite(value) || value <= 0) return '';
  return Math.floor(budget / value);
};

// An order count is a number of orders. Validated rather than parsed away as it is typed, so a
// seller who enters 2.5 is told what is wrong instead of watching the field rewrite itself. The API
// floors whatever arrives, so this is the form saying the same thing first.
const wholeOrdersRule = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  return Number.isInteger(Number(value)) ? undefined : 'Must be a whole number of orders';
};

/*
The order count limit a campaign may carry: no more orders than its budget could ever pay for.
Mirrors the same check in the API's createCampaign/editCampaign, so the form refuses what the save
would refuse. Applies to a product coupon and to a store-wide cashback alike - only the divisor
differs.

A GIFT is exempt: it spends no budget, so there is nothing to hold its count to and the count is the
only cap it has. Same when no budget is set on either of the others. The rule stays silent until the
figures it divides are on the form: until then there is no maximum to hold the entry to.
*/
const orderLimitWithinBudgetRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const values = allValues || {};
  if (values.type === 'gift') return undefined;
  const isCashback = values.tier === 'seller';
  const funded = isCashback ? derivedCashbackOrderLimit(values) : derivedCouponOrderLimit(values);
  if (funded === '') return undefined;
  if (Number(value) > funded) {
    // No figure is named. On a product coupon and a flat cashback the field is refilled from the
    // budget and the reward amount on every change to either, so the maximum is already the number
    // sitting in the box - repeating it in the error would only say the same thing twice. On a
    // percentage cashback the divisor is the smallest payout an order can draw, which the form
    // states beside the cashback fields themselves.
    return 'Max order count limit should be within budget';
  }
  return undefined;
};

/*
The LEAST a single qualifying order can draw from a store-wide cashback - the divisor behind its
order count limit. Mirrors `cashbackPerOrderMinimum` in the API's controllers/productCoupon.js.

  flat                   - the fixed amount every qualifying order takes; least and most are one.
  proportionate          - the payout at exactly the minimum order value (MOV x %). No qualifying
                           order is smaller, so none can earn less.
  minimum (pct + floor)  - the guaranteed minimum: a small order draws the floor rather than its
                           percentage.

The MAX cashback is deliberately not used here. It bounds what one order can COST, not how many
orders the budget can reach - and the count is a cap the seller chooses within what their money could
fund, not a forecast of what it will fund.
*/
const cashbackPerOrderMinimum = (values) => {
  if (values.cashback_type === 'flat') return Number(values.cashback_value);
  if (values.cashback_type === 'minimum') return Number(values.cashback_floor);
  if (values.cashback_type === 'proportionate') {
    return (Number(values.min_order_value) * Number(values.cashback_percent)) / 100;
  }
  return NaN;
};

// The most orders a store-wide cashback's budget could ever pay out on - the CEILING on its order
// count limit, and what that field is filled in with when the seller leaves it blank.
//
// Rounded first so it matches the rounded budget the API stores. Returns '' until both figures are
// present, so nothing is guessed from a half-filled form.
const derivedCashbackOrderLimit = (values) => {
  const budget = Math.round(Number(values.budget_total));
  const perOrder = cashbackPerOrderMinimum(values);
  if (!Number.isFinite(budget) || budget <= 0) return '';
  if (!Number.isFinite(perOrder) || perOrder <= 0) return '';
  return Math.floor(budget / perOrder);
};

// Drops the picked listing whenever the reward changes. A gift only runs on a listing sold in one
// fixed lot, so the coupon list and the gift list are different sets of listings - a pick made under
// one reward must not survive a switch to the other. The first render is skipped: nothing has
// changed yet there, and clearing then would wipe the form the moment it opens.
const ResetListingOnReward = ({ type, onChange }) => {
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    onChange();
    // only the reward matters here - onChange is a fresh closure on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);
  return null;
};

/*
Fills the order count in from what the budget funds, and keeps it there.

Every change to the figures the count is divided out of refills the field, so the number in the box is
always the maximum the campaign can carry. Which figures those are depends on the reward:

  product coupon        - budget / coupon value
  flat cashback         - budget / cashback amount
  percentage cashback   - budget / (min order value x cashback %), or / the floor on the floored type

The count is still the seller's to set - they may want to stop after 3 orders even though their budget
stretches to 10 - so a lower figure typed under the derived one stands, and survives until one of the
figures above moves again. It is a starting point that is kept current, not a locked value.

Refilling rather than leaving a stale count is the point: `orderLimitWithinBudgetRule` would reject a
count the budget no longer reaches, so the alternative to refilling is refusing on save. And on the
percentage types the divisor is assembled as the seller types - minimum order value, percentage,
floor - so a count that was right when it was typed stops being right two fields later.

The first render never overwrites, which is what keeps an edited campaign's saved count intact.

It replaces the `DerivedOrderLimit` that used to own the field outright on a cashback, back when a
cashback's count was a display figure that closed nothing.
*/
const PrefillOrderLimit = ({ value }) => {
  const form = useForm();
  const firstRender = useRef(true);
  useEffect(() => {
    const first = firstRender.current;
    firstRender.current = false;
    if (value === '') return;
    const current = form.getState().values.order_count_limit;
    const isEmpty = current === undefined || current === null || current === '';
    // Never overwritten on the first render: the derived figure is computed from the values the form
    // OPENS with, so overwriting then would throw away the count an edited campaign was saved with
    // before the seller has touched anything. From then on `value` only changes because the budget or
    // one of the reward figures did, which is exactly when it should refill.
    if (isEmpty || !first) {
      form.change('order_count_limit', value);
    }
  }, [value, form]);
  return null;
};

// ---- seller-tier (store-wide cashback) rules; these mirror validateSellerTier on the API ----

// minimum order value: at least 10,000 and a multiple of 1,000
const movRule = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const v = Number(value);
  if (v < 10000) return 'Minimum order value must be at least ₹10,000';
  if (v % 1000 !== 0) return 'Must be a multiple of ₹1,000';
  return undefined;
};

// proportionate / minimum cashback: 1% - 2%
const cashbackPercentRule = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const v = Number(value);
  if (v < 1 || v > 2) return 'Cashback must be between 1% and 2%';
  return undefined;
};

// a flat cashback (or a guaranteed floor) larger than the order that unlocks it could drive the
// payable to zero - the API caps it at runtime, but a campaign configured that way is a mistake
const notAboveMovRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const mov = Number(allValues && allValues.min_order_value);
  if (Number.isFinite(mov) && mov > 0 && Number(value) > mov) {
    return 'Cannot be greater than the minimum order value';
  }
  return undefined;
};

// Cashback earned on an order AT the minimum order value (MOV x cashback %). The floor and the max
// are both assessed against it, so both percentage types share this helper.
const movCashbackOf = (allValues) => {
  const mov = Number(allValues && allValues.min_order_value);
  const pct = Number(allValues && allValues.cashback_percent);
  if (!Number.isFinite(mov) || mov <= 0 || !Number.isFinite(pct) || pct <= 0) return null;
  return (mov * pct) / 100;
};

// The guaranteed minimum (percentage-with-floor only) must cover at least what a MOV-sized order
// earns by percentage - otherwise the "guarantee" is below the very floor of the percentage it sits on.
const minCashbackRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const movCashback = movCashbackOf(allValues);
  if (movCashback !== null && Number(value) < movCashback) {
    return `Must be at least ₹${Math.ceil(movCashback)} (min order value x cashback %)`;
  }
  return undefined;
};

// The max cashback (both percentage types) is the ceiling a buyer can earn on one order. It has to
// cover at least what an order AT the minimum order value earns (MOV x cashback %), and on the
// percentage-with-floor type it must also not sit below the guaranteed minimum (an empty band).
// Mirrors the API's proportionate / minimum checks in validateValueTier.
const maxCashbackRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const movCashback = movCashbackOf(allValues);
  if (movCashback !== null && Number(value) < movCashback) {
    return `Must be at least ₹${Math.ceil(movCashback)} (min order value x cashback %)`;
  }
  if (allValues && allValues.cashback_type === 'minimum') {
    const floor = Number(allValues.cashback_floor);
    if (Number.isFinite(floor) && floor > 0 && Number(value) < floor) {
      return 'Cannot be less than the guaranteed minimum';
    }
  }
  return undefined;
};

// A budget on a percentage cashback (proportionate or minimum) must be able to cover at least one max
// payout, so it cannot sit below the ceiling. Reddens the budget field and blocks submit; the campaign
// then auto-closes once the leftover budget drops back below the ceiling (API side).
const budgetAboveMaxRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (!allValues || (allValues.cashback_type !== 'proportionate' && allValues.cashback_type !== 'minimum')) return undefined;
  const max = Number(allValues.cashback_max);
  if (!Number.isFinite(max) || max <= 0) return undefined;   // the max field's own rule flags a missing max
  if (Math.round(Number(value)) < max) return 'Total budget must be greater than or equal to the max cashback';
  return undefined;
};

/*
Caps every numeric entry on this form at 2 decimal places.

Applied as react-final-form's `parse` rather than as a validator, so the third decimal simply never
lands in the field - the seller is stopped from typing it instead of being told off for it after the
fact. React restores the input's DOM value on a rejected keystroke, so the digit does not linger on
screen either.

Only a value that already HAS more than two decimals is rewritten; everything else is handed back
untouched, so half-typed entries ('12', '12.', '12.3') survive and the existing rules still get to
produce their own messages on anything that is not a number at all. '' becomes undefined, which is
what final-form's default parse does - fields treated as empty must stay empty rather than becoming
the string ''.
*/
const twoDecimals = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const tooPrecise = String(value).match(/^(-?\d*\.\d{2})\d+$/);
  return tooPrecise ? tooPrecise[1] : value;
};

// reactstrap text input that turns red (is-invalid) as soon as a value violates a rule
// NOTE: react-final-form's <Field> consumes the `type` prop and does not forward it
// to a custom component, so we pass the HTML input type via `inputType` instead.
const RSText = ({ input, meta, label, inputType, ...rest }) => {
  const showError = !!((meta.touched || meta.modified) && meta.error);
  return (
    <FormGroup>
      <Label for={input.name}>{label}</Label>
      <Input {...input} {...rest} type={inputType || 'text'} placeholder={label} invalid={showError} />
      <FormFeedback>{meta.error}</FormFeedback>
    </FormGroup>
  );
};

// reactstrap select that turns red (is-invalid) when its value violates a rule
const RSSelect = ({ input, meta, label, children, ...rest }) => {
  const showError = !!((meta.touched || meta.modified) && meta.error);
  return (
    <FormGroup>
      <Label for={input.name}>{label}</Label>
      <Input {...input} {...rest} type="select" invalid={showError}>{children}</Input>
      <FormFeedback>{meta.error}</FormFeedback>
    </FormGroup>
  );
};

const MyCoupons = () => {
  const [applied, setApplied] = useState(DEFAULT_FILTER);
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [searchedText, setSearchedText] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // null => create mode
  const [viewOnly, setViewOnly] = useState(false);    // true => read-only detail of an existing row
  const [listingOptions, setListingOptions] = useState([]);
  const [listingInput, setListingInput] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [listingTouched, setListingTouched] = useState(false);
  // Closing a campaign is permanent, so the Close button only opens this confirmation - the row
  // it holds is the campaign waiting to be closed, null when nothing is being confirmed.
  const [closingRow, setClosingRow] = useState(null);
  const [closeBusy, setCloseBusy] = useState(false);
  // Why the campaign is being closed early. Required by the API - a campaign stopped before any of
  // its thresholds was reached carries nothing that would explain it, so the seller has to say.
  // Cleared by whichever handler opens or dismisses the dialog, so text typed against one campaign
  // and then abandoned cannot be submitted against the next.
  const [closeReason, setCloseReason] = useState('');

  const setDraftField = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  const fetchList = async (p = 0, f = applied) => {
    setLoading(true);
    try {
      const body = { page: p + 1, perPage: PER_PAGE };
      if (f.status && f.status !== 'all') body.status = f.status;
      if (f.type && f.type !== 'all') body.type = f.type;
      if (f.search) body.searchText = f.search;
      if (f.from) body.from = f.from;
      if (f.to) body.to = f.to;
      const res = await AxiosRequest.axiosHelperFunc('post', 'productCoupon/my', '', body);
      const data = res && res.data;
      if (data && !data.error) {
        setRows(data.detail || []);
        setTotal(data.metadata && data.metadata.total ? data.metadata.total : 0);
      } else {
        NotificationManager.error(data && data.title ? data.title : 'Failed to load campaigns');
      }
    } catch (e) {
      NotificationManager.error('Failed to load campaigns');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchList(0, DEFAULT_FILTER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePage = (p) => {
    setPage(p);
    fetchList(p, applied);
  };

  const handleSearch = (text) => {
    const next = { ...applied, search: text || '' };
    setApplied(next);
    setSearchedText(text || '');
    setPage(0);
    fetchList(0, next);
  };

  const applyDraft = () => {
    const next = { ...applied, ...draft };
    setApplied(next);
    setPage(0);
    fetchList(0, next);
  };

  const resetDraft = () => {
    const next = { ...applied, ...DEFAULT_DRAFT };
    setDraft(DEFAULT_DRAFT);
    setApplied(next);
    setPage(0);
    fetchList(0, next);
  };

  const searchListings = async (val, rewardType) => {
    setListingInput(val || '');
    setListingTouched(true);
    if (!val) { setListingOptions([]); return; }
    try {
      // Campaign-only listing search: returns just the listings that are in Active Inventory, so a
      // campaign can never be attached to a listing buyers cannot see. The reward goes with it
      // because a gift narrows the list further - only listings sold in one fixed lot qualify.
      const res = await AxiosRequest.axiosHelperFunc('post', 'inventory/searchCampaignListing', '', { name: val, type: rewardType });
      setListingOptions(res && res.data && res.data.detail ? res.data.detail : []);
    } catch (e) {
      setListingOptions([]);
    }
  };

  const onSelectListing = (v) => {
    setSelectedListing(v);
    setListingInput(v && v.Products ? v.Products.name : '');
  };

  // Forget the picked listing entirely - selection, typed text and the options behind it.
  const clearListing = () => {
    setSelectedListing(null);
    setListingInput('');
    setListingOptions([]);
    setListingTouched(false);
  };

  const openCreate = () => {
    setEditingRow(null);
    setViewOnly(false);
    setSelectedListing(null);
    setListingInput('');
    setListingOptions([]);
    setListingTouched(false);
    setOpen(true);
  };

  // Pre-fill the campaign form from an existing row's data. Shared by Edit (writable) and the
  // row-click detail view (read-only) - both reuse the create-campaign popup so the seller sees
  // every field in the same layout.
  const fillFormFromRow = (row) => {
    setEditingRow(row);
    setSelectedListing(row.inventory_id && row.inventory_id._id ? { _id: row.inventory_id._id } : null);
    setListingInput(row.product_id && row.product_id.name ? row.product_id.name : '');
    setListingOptions([]);
    setListingTouched(false);
    setOpen(true);
  };

  // Edit is allowed only before admin acceptance (draft / pending); the listing and
  // type are fixed once created, so only the coupon/gift value and caps are editable.
  const openEdit = (row) => {
    setViewOnly(false);
    fillFormFromRow(row);
  };

  // Read-only detail of any campaign - opened by clicking its scope. Same popup as create/edit,
  // but every field is disabled and there is no submit (an active/closed campaign is not editable).
  const openView = (row) => {
    setViewOnly(true);
    fillFormFromRow(row);
  };

  const closeDialog = () => {
    setOpen(false);
    setViewOnly(false);
  };

  const submitCampaign = async (values) => {
    // On edit the reward type is fixed at creation and is not on the form.
    const isGift = editingRow ? editingRow.type === 'gift' : values.type === 'gift';
    // A gift carries no spend cap: the order count and the end date are its only thresholds, and
    // the budget field is not rendered for it. Everything else has all three, and closes on
    // whichever it reaches first - so any one of them is enough to bound the campaign.
    const capsValid = isGift
      ? !!(values.order_count_limit || values.end_date)
      : !!(values.budget_total || values.order_count_limit || values.end_date);
    if (!capsValid) {
      NotificationManager.warning(isGift
        ? 'Set at least one limit: order count or an end date'
        : 'Set at least one limit: total budget, order count, or an end date');
      return;
    }
    // Never send a budget on a gift - a stale value left in the form when the seller switched the
    // reward over to a gift would otherwise become a spend cap on it.
    const budgetTotal = isGift ? 0 : (values.budget_total ? Number(values.budget_total) : 0);

    // the reward fields differ per tier; the seller tier carries the MOV + cashback shape instead
    // of a per-unit coupon value
    const applyReward = (body, tier, type) => {
      if (type === 'gift') {
        // gift_image is a base64 data URL on a fresh pick, or the stored S3 URL when an existing
        // campaign is edited without touching the picture. The API handles both.
        body.gift = {
          description: values.gift_description,
          price: Number(values.gift_price),
          brand: values.gift_brand,
          image: values.gift_image
        };
      } else if (tier === 'seller') {
        body.cashback_type = values.cashback_type || 'flat';
        if (body.cashback_type === 'flat') {
          body.cashback_value = Number(values.cashback_value);
        } else {
          body.cashback_percent = Number(values.cashback_percent);
          if (body.cashback_type === 'minimum') body.cashback_floor = Number(values.cashback_floor);
          // both percentage types carry a mandatory ceiling on the per-order payout
          if (body.cashback_type === 'proportionate' || body.cashback_type === 'minimum') body.cashback_max = Number(values.cashback_max);
        }
      } else {
        body.coupon_value = Number(values.coupon_value);
      }
      if (tier === 'seller') body.min_order_value = Number(values.min_order_value);
      return body;
    };

    // ---- edit mode (campaign not yet approved) ----
    if (editingRow) {
      try {
        const body = {
          campaign_id: editingRow._id,
          budget_total: budgetTotal,
          order_count_limit: values.order_count_limit ? Number(values.order_count_limit) : 0,
          end_date: values.end_date || undefined
        };
        applyReward(body, editingRow.tier === 'seller' ? 'seller' : 'product', editingRow.type);
        const res = await AxiosRequest.axiosHelperFunc('post', 'productCoupon/edit', '', body);
        const data = res && res.data;
        if (data && !data.error) {
          NotificationManager.success(data.title || 'Campaign updated');
          setOpen(false);
          fetchList(page, applied);
        } else {
          NotificationManager.error(data && data.title ? data.title : 'Failed to update campaign');
        }
      } catch (e) {
        NotificationManager.error('Failed to update campaign');
      }
      return;
    }

    // ---- create mode ----
    const tier = values.tier === 'seller' ? 'seller' : 'product';

    // a store-wide campaign has no listing - it applies across everything the seller sells
    if (tier === 'product' && (!selectedListing || !selectedListing._id)) {
      NotificationManager.warning('Please select one of your listings');
      return;
    }
    try {
      // The discount reward is named after the tier: 'coupon' is a flat ₹ off each unit of one
      // listing, 'cashback' is what a store-wide campaign pays on an order over the MOV. Derived
      // from the tier rather than read straight off the form, so switching tier after picking the
      // reward cannot submit the other tier's spelling.
      const type = values.type === 'gift' ? 'gift' : (tier === 'seller' ? 'cashback' : 'coupon');
      const body = {
        tier,
        type,
        budget_total: budgetTotal,
        order_count_limit: values.order_count_limit ? Number(values.order_count_limit) : 0,
        end_date: values.end_date || undefined
      };
      if (tier === 'product') body.inventory_id = selectedListing._id;
      applyReward(body, tier, type);
      const res = await AxiosRequest.axiosHelperFunc('post', 'productCoupon/create', '', body);
      const data = res && res.data;
      if (data && !data.error) {
        NotificationManager.success(data.title || 'Campaign submitted for approval');
        setOpen(false);
        fetchList(0, applied);
        setPage(0);
      } else {
        NotificationManager.error(data && data.title ? data.title : 'Failed to create campaign');
      }
    } catch (e) {
      NotificationManager.error('Failed to create campaign');
    }
  };

  const askCloseCampaign = (row) => {
    setCloseReason('');
    setClosingRow(row);
  };

  const cancelCloseCampaign = () => {
    if (closeBusy) return;
    setClosingRow(null);
    setCloseReason('');
  };

  // Runs only once the seller has confirmed in the dialog above.
  const closeCampaign = async () => {
    const row = closingRow;
    if (!row) return;
    // Re-checked rather than left to the button's disabled state alone: this is the click that ends a
    // live campaign, and the API refuses a blank reason anyway - failing here costs one line and
    // spares the seller an error toast for something the form already knew.
    const reason = closeReason.trim();
    if (!reason) return;
    setCloseBusy(true);
    try {
      const res = await AxiosRequest.axiosHelperFunc('post', 'productCoupon/close', '', { campaign_id: row._id, close_reason: reason });
      const data = res && res.data;
      if (data && !data.error) {
        NotificationManager.success(data.title || 'Campaign closed');
        setClosingRow(null);
        setCloseReason('');
        fetchList(page, applied);
      } else {
        NotificationManager.error(data && data.title ? data.title : 'Failed to close');
      }
    } catch (e) {
      NotificationManager.error('Failed to close campaign');
    }
    setCloseBusy(false);
  };

  // How the reward reads depends on the tier: a product coupon is a flat ₹ off each unit, a
  // store-wide campaign is a cashback earned by clearing a minimum order value.
  const cashbackText = (row) => {
    if (row.cashback_type === 'flat') return `${money(row.cashback_value)} cashback`;
    // Both percentage types are described the same way - the band they pay in, floor and ceiling.
    if (row.cashback_type === 'proportionate' || row.cashback_type === 'minimum') {
      const band = cashbackBand(row.cashback_type, row.cashback_floor, row.cashback_max);
      return band
        ? `${row.cashback_percent}% cashback (${band})`
        : `${row.cashback_percent}% cashback`;
    }
    return '-';
  };

  const valueCell = (row) => {
    const overMov = row.tier === 'seller' && row.min_order_value > 0
      ? <span className="text-muted" style={{ fontSize: '12px' }}> on orders over {money(row.min_order_value)}</span>
      : null;
    if (row.type === 'gift') {
      const gift = `🎁 ${row.gift && row.gift.description ? row.gift.description : '-'}${row.gift && row.gift.price != null ? ` worth ${money(row.gift.price)}` : ''}${row.gift && row.gift.brand ? ` (${row.gift.brand})` : ''}`;
      // The delivery cost Medimny agreed on THIS campaign when they approved it - what the seller is
      // deducted per gift order, plus GST. Approval is the first moment the figure exists, so a
      // pending campaign says it is still to be set rather than showing a number: the create form
      // told the seller a cost would apply, and this is where they find out what it turned out to be.
      const deliveryCost = row.gift_delivery_cost > 0
        ? <span className="text-muted" style={{ fontSize: '12px' }}> · {money(row.gift_delivery_cost)} + GST delivery cost per gift order</span>
        : (row.status === 'pending'
          ? <span className="text-muted" style={{ fontSize: '12px' }}> · delivery cost is set when Medimny approves this campaign</span>
          : null);
      return <span>{gift}{overMov}{deliveryCost}</span>;
    }
    if (row.tier === 'seller') {
      return <span><strong>{cashbackText(row)}</strong>{overMov}</span>;
    }
    return <strong>{money(row.coupon_value)} off</strong>;
  };

  // A store-wide campaign has no product. Saying so beats rendering a bare '-', which reads as a bug.
  // The scope is clickable: it opens the campaign's full detail in the create-campaign popup (read-only).
  const scopeCell = (row) => {
    const label = row.tier === 'seller'
      ? <span>All products <span className="text-muted" style={{ fontSize: '12px' }}>(store-wide)</span></span>
      : (row.product_id && row.product_id.name ? row.product_id.name : '-');
    return (
      <span
        className="text-primary"
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={() => openView(row)}
        title="View campaign details"
      >
        {label}
      </span>
    );
  };

  const validity = (row) => {
    const start = row.start_date ? moment(row.start_date).format('DD MMM YY') : '-';
    const end = row.end_date ? moment(row.end_date).format('DD MMM YY') : 'No end';
    return `${start} → ${end}`;
  };

  // Grouped Edit / Close actions, styled to match the Inventory page button group.
  // Edit is offered only before admin acceptance (draft / pending); Close while running.
  const actionButtons = (row) => {
    const canEdit = ['draft', 'pending'].includes(row.status);
    const canClose = ['pending', 'active'].includes(row.status);
    if (!canEdit && !canClose) return <span>-</span>;
    return (
      <ButtonGroup color="primary" aria-label="coupon actions">
        {canEdit ? <Button className={'text-primary'} onClick={() => openEdit(row)}>Edit</Button> : null}
        {canClose ? <Button className={'text-danger'} onClick={() => askCloseCampaign(row)}>Close</Button> : null}
      </ButtonGroup>
    );
  };

  const plain = { filter: false, sort: false };
  const columns = [
    { name: "product", label: "Scope", options: plain },
    { name: "type", label: "Type", options: plain },
    { name: "value", label: "Reward", options: plain },
    { name: "budget", label: "Budget (spent/total)", options: plain },
    { name: "orders", label: "Orders (used/limit)", options: plain },
    { name: "validity", label: "Validity", options: plain },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        filterType: 'custom',
        sort: false,
        customFilterListRender: () => false,
        filterOptions: {
          names: [],
          logic() { },
          display: () => (
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 240, paddingTop: 8 }}>
              <TextField
                select label="Status" value={draft.status}
                onChange={(e) => setDraftField('status', e.target.value)}
                fullWidth variant="standard"
              >
                {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{cap(s)}</MenuItem>)}
              </TextField>
              <TextField
                select label="Type" value={draft.type}
                onChange={(e) => setDraftField('type', e.target.value)}
                fullWidth variant="standard"
              >
                {TYPE_OPTIONS.map((t) => <MenuItem key={t} value={t}>{cap(t)}</MenuItem>)}
              </TextField>
              <TextField
                type="date" label="From" value={draft.from}
                onChange={(e) => setDraftField('from', e.target.value)}
                InputLabelProps={{ shrink: true }} fullWidth variant="standard"
              />
              <TextField
                type="date" label="To" value={draft.to}
                onChange={(e) => setDraftField('to', e.target.value)}
                InputLabelProps={{ shrink: true }} fullWidth variant="standard"
              />
            </Box>
          )
        }
      }
    },
    { name: "action", label: "Action", options: plain }
  ];

  const options = {
    searchPlaceholder: 'Press Enter to Search',
    filterType: 'dropdown',
    responsive: 'scroll',
    print: false,
    download: false,
    selectableRows: false,
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    search: true,
    searchText: searchedText,
    viewColumns: false,
    rowHover: false,
    filter: true,
    sort: false,
    page: page,
    rowsPerPage: PER_PAGE,
    rowsPerPageOptions: [],
    count: total,
    fixedHeader: false,
    serverSide: true,
    textLabels: { filter: { all: "", title: "FILTERS", reset: "" } },
    customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
      return (
        <div className="p-2">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button fullWidth type="button" variant="contained" color="primary" onClick={() => { applyDraft(); applyNewFilters(); }}>APPLY</Button>
            </Grid>
            <Grid item xs={6}>
              <Button fullWidth type="button" variant="outlined" onClick={() => { resetDraft(); applyNewFilters(); }}>RESET</Button>
            </Grid>
          </Grid>
        </div>
      );
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          changePage(tableState.page);
          break;
        case 'search':
          onkeypress = (e) => {
            if (e.key === 'Enter') {
              handleSearch(tableState.searchText);
            }
          };
          break;
        case 'onSearchClose':
          handleSearch('');
          break;
        default:
          break;
      }
    },
    customFooter: (count, pg, rowsPerPage, changeRowsPerPage, changePg) => {
      return <div className="p-4 d-flex justify-content-end">
        <PaginationCount page={pg} rowsPerPage={rowsPerPage} count={count} />
        <Pagination count={Math.ceil(count / rowsPerPage)} page={pg + 1} onChange={(e, p) => { changePg(p - 1) }} color="primary" />
      </div>;
    }
  };

  let data = [];
  rows.forEach((row) => {
    data.push([
      scopeCell(row),
      <span style={{ textTransform: 'capitalize' }}>{row.type}</span>,
      valueCell(row),
      // A gift carries no spend cap at all - it is held to its order count and its end date - so the
      // budget column has nothing to report on one and a 0 / ∞ there would read as a real figure.
      row.type === 'gift'
        ? '-'
        : `${money(row.budget_spent)} / ${row.budget_total > 0 ? money(row.budget_total) : '∞'}`,
      `${row.order_count_used || 0} / ${row.order_count_limit > 0 ? row.order_count_limit : '∞'}`,
      validity(row),
      <div>
        <span className={`badge text-uppercase ${statusStyle(row.status)}`}>{row.status}</span>
        {row.status === 'rejected' && row.rejection_reason ?
          <p className="text-danger mb-0" style={{ fontSize: 11 }}>{row.rejection_reason}</p> : null}
      </div>,
      actionButtons(row)
    ]);
  });

  return (
    <SellerContainer>
      <ContainerHeader title="My Coupons & Gifts" title1="coupons" />

      {loading ?
        <div className="loader-view" style={{ height: 'calc(100vh - 259px)' }}>
          <CircularProgress />
        </div> :
        <ThemeProvider theme={createTheme({ components: styles })}>
          <MUIDataTable
            title={<Button className={'text-primary'} onClick={openCreate}>Create Campaign +</Button>}
            data={data}
            columns={columns}
            options={options}
          />
        </ThemeProvider>
      }

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <Form
          key={editingRow ? editingRow._id : 'create'}
          onSubmit={submitCampaign}
          initialValues={editingRow ? {
            tier: editingRow.tier === 'seller' ? 'seller' : 'product',
            type: editingRow.type,
            coupon_value: editingRow.coupon_value || '',
            min_order_value: editingRow.min_order_value > 0 ? editingRow.min_order_value : '',
            cashback_type: editingRow.cashback_type || 'flat',
            cashback_value: editingRow.cashback_value > 0 ? editingRow.cashback_value : '',
            cashback_percent: editingRow.cashback_percent > 0 ? editingRow.cashback_percent : '',
            cashback_floor: editingRow.cashback_floor > 0 ? editingRow.cashback_floor : '',
            cashback_max: editingRow.cashback_max > 0 ? editingRow.cashback_max : '',
            gift_description: editingRow.gift && editingRow.gift.description ? editingRow.gift.description : '',
            gift_price: editingRow.gift && editingRow.gift.price != null ? editingRow.gift.price : '',
            gift_brand: editingRow.gift && editingRow.gift.brand ? editingRow.gift.brand : '',
            gift_image: editingRow.gift && editingRow.gift.image ? editingRow.gift.image : '',
            budget_total: editingRow.budget_total > 0 ? editingRow.budget_total : '',
            order_count_limit: editingRow.order_count_limit > 0 ? editingRow.order_count_limit : '',
            end_date: editingRow.end_date ? moment(editingRow.end_date).format('YYYY-MM-DD') : ''
          } : { tier: 'product', type: 'coupon', cashback_type: 'flat' }}
          render={({ handleSubmit, values, invalid, submitting }) => {
            const type = values.type || 'coupon';
            const tier = values.tier === 'seller' ? 'seller' : 'product';
            const isSeller = tier === 'seller';
            const cashbackType = values.cashback_type || 'flat';
            // A product coupon spends a fixed rupee amount per order, so its budget puts a CEILING on
            // its order limit - but the limit within that is the seller's to choose, and is a real
            // threshold: the campaign closes on whichever of the three caps it reaches first.
            const isProductCoupon = !isSeller && type !== 'gift';
            const derivedLimit = isProductCoupon ? derivedCouponOrderLimit(values) : '';
            // A gift is not paid for in rupees, so a spend cap means nothing on one: the order count
            // and the end date are its only thresholds, and the budget field is hidden below.
            // The budget counts as a limit only once it ROUNDS to something: the API stores it
            // rounded and then tests the stored figure, so a budget under ₹0.50 is no cap at all
            // there. Testing the raw entry here would pass the form and fail the save.
            const budgetIsACap = Math.round(Number(values.budget_total)) > 0;
            // A store-wide CASHBACK carries all three caps, like a product coupon: its order count is
            // the seller's to set - bounded by what the budget could pay out on - and it closes the
            // campaign like any other threshold. It was briefly a disabled, derived display figure;
            // it is a cap again.
            const isValueCashback = isSeller && type !== 'gift';
            // The most orders the budget could pay this cashback on - the ceiling on the count below,
            // and the figure that field is refilled with as these values change.
            const cashbackFundsOrders = isValueCashback ? derivedCashbackOrderLimit(values) : '';
            // Every campaign now closes on whichever of its caps it reaches first, so any one of them
            // on its own is enough to bound it. A gift simply has no budget to offer.
            const capsValid = type === 'gift'
              ? !!(values.order_count_limit || values.end_date)
              : !!(budgetIsACap || values.order_count_limit || values.end_date);
            // Lot size (min order quantity) of the picked listing - the coupon is granted per lot, so
            // the seller needs to see it when setting the coupon value. Comes from the freshly picked
            // listing on create, and from the snapshot (coupon_min_qty) when viewing/editing an
            // existing campaign whose listing object is no longer loaded.
            const lotSize = selectedListing && selectedListing.min_order_quantity != null
              ? selectedListing.min_order_quantity
              : (editingRow && editingRow.inventory_id && editingRow.inventory_id.min_order_quantity != null
                  ? editingRow.inventory_id.min_order_quantity
                  : (editingRow && editingRow.coupon_min_qty != null ? editingRow.coupon_min_qty : null));
            return (
              <form noValidate onSubmit={handleSubmit} autoComplete="off">
                <DialogTitle>{viewOnly ? 'Campaign details' : editingRow ? 'Edit campaign' : 'Create campaign'}</DialogTitle>
                <DialogContent>
                  {/* In view mode a native disabled fieldset makes every nested control read-only at
                      once, so the seller sees the full campaign in the same layout without editing it. */}
                  <fieldset disabled={viewOnly} style={{ border: 0, padding: 0, margin: 0, minInlineSize: 'auto' }}>
                  <Row>
                    <Col xs={12}>
                      {/* The tier fixes what the campaign attaches to, so it cannot change after
                          creation. Worded as scope, not as tier: 'Seller level' is our own
                          vocabulary and says nothing to a seller who already knows they are the
                          seller. 'Store-wide' matches the wording the list uses for the same tier
                          ('All products (store-wide)'). */}
                      <Field name="tier" component={RSSelect} label="Campaign scope" disabled={!!editingRow}>
                        <option value="product">Single product</option>
                        <option value="seller">Store-wide</option>
                      </Field>
                    </Col>

                    <Col xs={12}>
                      <Field name="type" component={RSSelect} label="Reward" disabled={!!editingRow}>
                        {/* the discount reward carries the tier's own name, so what is submitted
                            matches what the seller was shown */}
                        <option value={isSeller ? 'cashback' : 'coupon'}>{isSeller ? 'Cashback' : 'Coupon (flat ₹ off)'}</option>
                        <option value="gift">Gift</option>
                      </Field>
                      {/* The cost used to ride along inside the option label, where it was read once
                          and then scrolled past. It belongs here instead: it only exists once Gift
                          is the chosen reward, and this is the moment the seller is deciding.

                          No figure is named, because at this moment there is none to name. The
                          delivery cost is set PER CAMPAIGN by Medimny when this campaign is approved,
                          so it does not exist until then - and quoting a global or per-seller number
                          here would be quoting a rate that is not what this campaign is charged. The
                          approved amount appears on the campaign's own row once it goes live. Saying
                          nothing at all would let a seller commit to a gift believing it is free. */}
                      {type === 'gift' ?
                        <div style={{ fontSize: 12, marginTop: -8, marginBottom: 8, color: '#b26a00' }}>
                          A gift delivery cost plus GST is deducted from your settlement for every order
                          that carries this gift. Medimny sets the amount for this campaign when it is
                          approved, and it is shown on the campaign once live.
                        </div> : null}
                    </Col>

                    {/* A store-wide campaign has no listing - it applies to everything this seller sells. */}
                    {!isSeller ?
                      <Col xs={12}>
                        {editingRow ?
                          <FormGroup>
                            <Label>Listing</Label>
                            <Input type="text" value={listingInput} disabled readOnly />
                          </FormGroup> :
                          <React.Fragment>
                          {/* Switching between a coupon and a gift changes which listings qualify,
                              so a pick made under the previous reward is dropped. */}
                          <ResetListingOnReward type={type} onChange={clearListing} />
                          <AutoCompleteSearch
                            searchType="other"
                            value={listingInput}
                            options={listingOptions}
                            // On a gift the picker is narrowed to listings sold in one fixed lot, so
                            // say so where the seller is about to search - an unexplained short list
                            // reads as missing stock.
                            label={type === 'gift' ?
                              <React.Fragment>
                                Select your listing
                                <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                                  - only listings whose lot size equals their maximum order quantity are shown
                                </span>
                              </React.Fragment> : 'Select your listing'}
                            handleDataChange={(v) => onSelectListing(v)}
                            handleChange={(e, v) => searchListings(v, type)}
                            error={listingTouched && !selectedListing}
                            helperText="Please select your listing from the list"
                            // Show each listing's stock and order limits next to its name, so the
                            // seller can tell apart listings of the same product by what is left of
                            // them and by the quantities a buyer can order.
                            renderOption={(optProps, option) => (
                              <li {...optProps} key={option._id}>
                                {option && option.Products && option.Products.name ? option.Products.name : ''}
                                <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                                  (Available: {option.quantity != null ? option.quantity : '-'}, Min: {option.min_order_quantity != null ? option.min_order_quantity : '-'}, Max: {option.max_order_quantity != null ? option.max_order_quantity : '-'})
                                </span>
                              </li>
                            )}
                          />
                          </React.Fragment>
                        }
                      </Col> : null}

                    {isSeller ?
                      <Col xs={12}>
                        <Field
                          id="min_order_value" name="min_order_value" inputType="number"
                          component={RSText} label="Minimum order value (₹) - min 10,000, multiple of 1,000"
                          parse={twoDecimals}
                          validate={composeValidators(required, number, movRule)}
                        />
                        {/* <Label className="text-muted" style={{ fontSize: 12, marginTop: -8, display: 'block' }}>
                          Assessed per order, on the value excluding GST and after any product coupon.
                        </Label> */}
                      </Col> : null}

                    {/* Read-only lot size of the picked listing. It belongs above BOTH rewards on a
                        product campaign: a coupon is granted per lot (floor(qty / lot size)), and a
                        gift is granted per lot too - which is why a gift may only run on a listing
                        whose lot size IS its maximum order quantity, so one order earns one gift.
                        A store-wide campaign has no listing and therefore no lot to show. */}
                    {!isSeller && lotSize != null ?
                      <Col xs={12}>
                        <FormGroup>
                          <Label>Lot size (min order quantity)</Label>
                          <Input type="text" value={lotSize} disabled readOnly />
                        </FormGroup>
                      </Col> : null}

                    {type === 'gift' ?
                      <React.Fragment>
                        <Col xs={12}>
                          <Field id="gift_description" name="gift_description" inputType="text"
                            component={RSText} label="Gift description" validate={required} />
                        </Col>
                        <Col xs={6}>
                          <Field id="gift_price" name="gift_price" inputType="number"
                            component={RSText} label="Gift price (₹)" parse={twoDecimals}
                            validate={composeValidators(required, number)} />
                        </Col>
                        <Col xs={6}>
                          <Field id="gift_brand" name="gift_brand" inputType="text"
                            component={RSText} label="Gift brand" validate={required} />
                        </Col>
                        {/* Mandatory picture of the gift, uploaded the same way as an invoice: tap the
                            icon, pick a file, it is posted as base64 and stored on S3 by the API.
                            Applies to both seller-funded tiers - a product gift and a store-wide one. */}
                        <Col xs={12} className="mt-2">
                          <Field name="gift_image" component={FieldFileInput}
                            label="Gift image" accept=".jpg, .png, .jpeg" preview
                            viewOnly={viewOnly}
                            validate={requiredImage} />
                        </Col>
                      </React.Fragment>
                    : isSeller ?
                      <React.Fragment>
                        <Col xs={12}>
                          <Field name="cashback_type" component={RSSelect} label="Cashback type">
                  <option value="flat">Flat (₹)</option>
                      <option value="proportionate">Percentage (%)</option>
                      <option value="minimum">Percentage with floor</option>
                          </Field>
                        </Col>
                        {cashbackType === 'flat' ?
                          <Col xs={12}>
                            <Field id="cashback_value" name="cashback_value" inputType="number"
                              component={RSText} label="Cashback amount (₹)" parse={twoDecimals}
                              validate={composeValidators(required, number, notAboveMovRule)} />
                          </Col> :
                          <React.Fragment>
                            <Col xs={6}>
                              <Field id="cashback_percent" name="cashback_percent" inputType="number"
                                component={RSText} label="Cashback (%) - 1 to 2" parse={twoDecimals}
                                validate={composeValidators(required, number, cashbackPercentRule)} />
                            </Col>
                            {cashbackType === 'minimum' ?
                              <Col xs={6}>
                                {/* No helper line, as under the ceiling beside it: what the floor means
                                    is in the label, and what it must be at least is said by
                                    minCashbackRule, in red, on the entry that breaks it. */}
                                <Field id="cashback_floor" name="cashback_floor" inputType="number"
                                  component={RSText} label="Min cashback (₹)" parse={twoDecimals}
                                  validate={composeValidators(required, number, minCashbackRule, notAboveMovRule)} />
                              </Col> : null}
                            {/* Both percentage types: the ceiling a buyer earns on one order, however
                                large. Mandatory - it is what bounds the campaign's cost. */}
                            <Col xs={6}>
                              {/* No helper line: what the ceiling means is in the label, and what it
                                  must be at least is said by maxCashbackRule, in red, on the entry
                                  that breaks it. */}
                              <Field id="cashback_max" name="cashback_max" inputType="number"
                                component={RSText} label="Max cashback (₹)" parse={twoDecimals}
                                validate={composeValidators(required, number, maxCashbackRule)} />
                            </Col>
                          </React.Fragment>
                        }
                        {/* Recomputed on every keystroke from the live form values, so the seller sees
                            what a decimal actually does as they type it rather than after saving. It
                            is a statement of what will happen, not a validation error - a decimal is a
                            legal entry, it just is not the figure that gets paid. */}
                        {cashbackRoundingNote(values) ?
                          <Col xs={12}>
                            <div style={{ fontSize: 12, marginTop: -8, marginBottom: 8, color: '#b26a00' }}>
                              {cashbackRoundingNote(values)}
                            </div>
                          </Col> : null}
                      </React.Fragment>
                    :
                      <React.Fragment>
                        <Col xs={12}>
                          <Field
                            id="coupon_value" name="coupon_value" inputType="number"
                            component={RSText} label="Coupon value (₹)  - min 20, multiple of 5"
                            parse={twoDecimals}
                            validate={composeValidators(required, number, couponValueRule, withinBudgetRule)}
                          />
                        </Col>
                      </React.Fragment>
                    }

                    <Col xs={12} className="mt-2 mb-1">
                      <Label>Set at least one limit (any combination):</Label>
                    </Col>
                    {/* Every campaign closes on whichever of its caps it reaches first - said here,
                        once, for the whole row rather than under each field. A gift has no budget, so
                        it is told about the two caps it does have. */}
                    <Col xs={12} className="mb-1">
                      <Label className="text-muted" style={{ fontSize: 12, display: 'block' }}>
                        {type === 'gift'
                          ? 'This campaign closes automatically when the order count is reached or the end date passes - whichever comes first.'
                          : 'This campaign closes automatically when the budget runs out, the order count is reached, or the end date passes - whichever comes first.'}
                      </Label>
                    </Col>
                    {/* A gift campaign spends no rupees, so it carries no budget cap - the order
                        count and the end date are the only thresholds it can be held to. */}
                    {type !== 'gift' ?
                      <Col xs={6}>
                        <Field id="budget_total" name="budget_total" inputType="number"
                          component={RSText} label="Total budget (₹)" parse={twoDecimals}
                          validate={composeValidators(number, budgetAboveMaxRule)} />
                        {/* No line under the budget saying what it funds. The order count beside it is
                            filled in with that same figure, so the budget's reach is already on the
                            form as a number the seller can act on rather than as prose. */}
                      </Col> : null}
                    <Col xs={type === 'gift' ? 12 : 6}>
                      <Field id="order_count_limit" name="order_count_limit" inputType="number"
                        component={RSText} label="Order count limit" parse={twoDecimals}
                        validate={composeValidators(number, wholeOrdersRule, orderLimitWithinBudgetRule)} />
                      {/* No helper line under the count. It is refilled from the budget and the reward
                          figures on every change to any of them, so the number in the box IS what the
                          budget funds and a line restating it said the same thing twice. Going above it
                          reddens the field through orderLimitWithinBudgetRule. */}
                    </Col>
                    {/* Refilled whenever the budget or a reward figure changes, on every budgeted
                        reward: a coupon divides by its value, a flat cashback by its amount, a
                        percentage one by what an order at the minimum order value earns. So the field
                        always shows the maximum the campaign may carry, and a lower count typed under
                        it stands until one of those figures moves. */}
                    {!viewOnly && (isProductCoupon || isValueCashback)
                      ? <PrefillOrderLimit value={isProductCoupon ? derivedLimit : cashbackFundsOrders} />
                      : null}
                    <Col xs={12}>
                      <Field id="end_date" name="end_date" inputType="date"
                        component={RSText} label="End date" />
                    </Col>
                    {/* Sits under the whole caps row rather than beside the budget box, so it reads
                        the same however the row is laid out. Never shown on a gift, which has no
                        budget field to have typed a decimal into. */}
                    {type !== 'gift' && budgetRoundingNote(values) ?
                      <Col xs={12}>
                        <div style={{ fontSize: 12, marginTop: -8, marginBottom: 8, color: '#b26a00' }}>
                          {budgetRoundingNote(values)}
                        </div>
                      </Col> : null}
                    {!capsValid ?
                      <Col xs={12}><Label className="text-danger">
                        {type === 'gift'
                          ? 'Set at least one limit (order count or end date).'
                          : 'Set at least one limit (budget, order count or end date).'}

                      </Label></Col> : ''}

                    {/* Why the campaign ended, for one that has - the same pair the admin's detail
                        popup carries, since a seller looking at a stopped campaign wants exactly what
                        an admin does: who ended it and what for. The label names the party and the
                        value is their reason - free text when a seller or an admin pulled the plug
                        early, one of the fixed system strings when a budget, an order count or the
                        end date ran out.

                        A boxed read-only input among the campaign's own fields, like every other
                        value on this popup, rather than a callout of its own. Absent on rows closed
                        before the field existed, which render nothing rather than an empty box. */}
                    {editingRow && ['closed', 'expired'].includes(editingRow.status) && editingRow.close_reason ?
                      <Col xs={12}>
                        <FormGroup>
                          <Label>{closedByLabel(editingRow)}</Label>
                          <Input type="text" value={editingRow.close_reason} readOnly disabled />
                        </FormGroup>
                      </Col> : null}

                    {/* A rejected campaign never ran, so it has no closure to explain - it carries the
                        admin's rejection reason instead, and the two are mutually exclusive by status.
                        The list prints it under the status badge, where a long one is clipped; this is
                        where the seller reads it in full. */}
                    {editingRow && editingRow.status === 'rejected' && editingRow.rejection_reason ?
                      <Col xs={12}>
                        <FormGroup>
                          <Label>Rejected by Medimny</Label>
                          <Input type="text" value={editingRow.rejection_reason} readOnly disabled />
                        </FormGroup>
                      </Col> : null}

                    {/* What each cashback type means, so the seller picks the right one. Only shown for a
                        store-wide cashback (a gift/product coupon has no type). Same info callout style as
                        the admin platform form: tinted box, muted text. */}
                    {isSeller && type !== 'gift' ?
                      <Col xs={12} className="mt-2">
                        <div className="p-2" style={{ background: '#eef3fb', borderRadius: 4, fontSize: 12.5 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Cashback types</div>
                          <div className="text-muted" style={{ lineHeight: 1.7 }}>
                            <div><strong>Flat (₹)</strong> - a fixed rupee amount off every qualifying order (e.g. ₹200 off).</div>
                            <div><strong>Percentage (%)</strong> - a percentage of the order value (e.g. 2% of a ₹10,000 order = ₹200).</div>
                            <div><strong>Percentage with floor</strong> - a percentage, but never less than a guaranteed minimum (e.g. 2%, but at least ₹150).</div>
                          </div>
                        </div>
                      </Col> : null}
                  </Row>
                  </fieldset>
                </DialogContent>
                <DialogActions className="pr-4">
                  <Button onClick={closeDialog} color="secondary">{viewOnly ? 'Close' : 'Cancel'}</Button>
                  {/* No submit in view mode - it is a read-only detail of an existing campaign.
                      The listing requirement applies to the product tier only - a store-wide
                      campaign has no listing, and gating on it would make the form unsubmittable */}
                  {!viewOnly ?
                    <Button type="submit" color="primary" disabled={invalid || (!editingRow && !isSeller && !selectedListing) || !capsValid || submitting}>
                      {submitting ? 'Saving…' : editingRow ? 'Save changes' : 'Submit for approval'}
                    </Button> : null}
                </DialogActions>
              </form>
            );
          }}
        />
      </Dialog>

      {/* Close confirmation. Closing is final - the campaign cannot be reopened, and on a product
          campaign a 7-day cooldown follows - so the seller is told what it costs before they commit.
          The cooldown is stamped on the one listing that ran the coupon, but createCampaign checks it
          across ALL of this seller's listings of the product (controllers/productCoupon.js), so the
          wording has to say product, not listing: starting again on another batch is blocked too. */}
      <Dialog open={!!closingRow} onClose={cancelCloseCampaign} fullWidth={true} maxWidth={'sm'}>
        <DialogTitle className="pb-0">CLOSE CAMPAIGN</DialogTitle>
        <DialogContent>
          <div className="mt-2" style={{ fontSize: 16 }}>
            Are you sure you want to close this campaign
            {closingRow && closingRow.tier !== 'seller' && closingRow.product_id && closingRow.product_id.name
              ? <span> on <strong>{closingRow.product_id.name}</strong></span>
              : null} ?
          </div>
          <div className="text-muted mt-2" style={{ fontSize: 14, lineHeight: 1.6 }}>
            This cannot be undone. The campaign stops immediately and buyers can no longer earn its
            reward.
            {closingRow && closingRow.tier !== 'seller'
              ? ' The product cannot carry another campaign for the next 7 days - the cooldown covers every listing of this product, not just this one.'
              : ''}
          </div>
          {/* The reason is mandatory, and the helper text says why rather than just that it is: a
              campaign that ends on its budget, its order count or its end date records which of those
              it was, but one stopped by hand records nothing at all unless the seller says so. */}
          {/* Boxed reactstrap control, same as the campaign form's own fields (RSText above) - a
              floating-label MUI input here would be the only one of its kind on the screen. */}
          <FormGroup className="mt-3">
            <Label for="close_reason">Reason for closing</Label>
            <Input
              type="textarea" rows={2} autoFocus
              id="close_reason" name="close_reason"
              placeholder="e.g. pricing revised, stock no longer available"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              disabled={closeBusy}
              maxLength={500}
            />
            <FormText>Required. This campaign is being closed before any of its limits was reached, so the record needs your reason.</FormText>
          </FormGroup>
        </DialogContent>
        <DialogActions className="pr-4">
          <Button onClick={cancelCloseCampaign} color="secondary" disabled={closeBusy}>Cancel</Button>
          {/* The colour class is dropped while the button is disabled, and that is load-bearing:
              Bootstrap's `.text-danger` sets `color` with `!important`, which MUI's disabled styling
              cannot override - leaving the class on would give a dead button a live red look. */}
          <Button
            onClick={closeCampaign}
            className={closeBusy || !closeReason.trim() ? '' : 'text-danger'}
            disabled={closeBusy || !closeReason.trim()}
          >
            {closeBusy ? 'Closing…' : 'Close campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </SellerContainer>
  );
};

export default MyCoupons;
